import React, { useEffect, useState } from "react";
import { Box, TextField, CircularProgress } from "@mui/material";
import Modal, { ModalHead, ModalBody, ModalFoot } from "./Modal";
import BtnPrimary from "./PrimaryButton";
import BtnSecondary from "./SecondaryButton";
import ErrorMsg from "./ErrorMsg";
import InlineMessage from "./InlineMessage";
import { getExitReadiness } from "../services/employee";
import { tokens } from "../theme";

/**
 * Marking somebody as having left.
 *
 * The last act, and the one that is hardest to walk back: afterwards they are
 * inactive, out of the directory and off payroll's radar, and whatever was
 * unfinished stays unfinished because nobody is chasing a name that is no
 * longer in a list.
 *
 * So what is outstanding is fetched and shown BEFORE anything is pressed.
 * Finding out by attempting the thing and being refused is the wrong way
 * round, and it is the shape that makes people click twice without reading.
 *
 * Overriding is allowed and costs a sentence. Some exits really are messy —
 * a laptop written off, a settlement handled outside the system — and a gate
 * that cannot be passed gets worked around instead of recorded.
 */
const ConfirmExitDialog = ({ employee, open, onClose, onConfirm }) => {
  const [blockers, setBlockers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !employee?._id) return undefined;
    let active = true;
    setLoading(true);
    setReason("");
    setError("");
    getExitReadiness(employee._id)
      .then((res) => active && setBlockers(res?.data?.blockers || []))
      // If the check cannot run, treat it as "nothing known" rather than
      // "nothing outstanding" — the footer says which it was.
      .catch(() => active && setBlockers(null))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [open, employee?._id]);

  const blocked = Array.isArray(blockers) && blockers.length > 0;

  const submit = async () => {
    if (blocked && !reason.trim()) {
      setError("Say why this exit is being closed with items outstanding");
      return;
    }
    setBusy(true);
    try {
      await onConfirm({ force: blocked || undefined, reason: reason.trim() || undefined });
    } finally {
      setBusy(false);
    }
  };

  const name = employee?.fname || "this employee";

  return (
    <Modal open={open} onClose={onClose} width="32rem">
      <ModalHead
        title="Mark as left?"
        subtitle={`${employee?.fname || ""} ${employee?.lname || ""}`.trim()}
        onClose={onClose}
      />
      <ModalBody>
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            {blockers === null && (
              <Box sx={{ mb: "1.25rem" }}>
                <InlineMessage tone="warning">
                  The outstanding-items check could not run, so this is going ahead
                  without it. Worth confirming the laptop is back and the settlement
                  is paid.
                </InlineMessage>
              </Box>
            )}

            {blocked && (
              <Box sx={{ mb: "1.25rem" }}>
                <InlineMessage tone="warning" title="This exit is not finished:">
                  <Box component="ul" sx={{ m: 0, pl: "1.1rem" }}>
                    {blockers.map((b) => (
                      <li key={b.code}>
                        {b.label}
                        {b.detail && (
                          <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>{b.detail}</Box>
                        )}
                      </li>
                    ))}
                  </Box>
                </InlineMessage>
              </Box>
            )}

            {Array.isArray(blockers) && blockers.length === 0 && (
              <Box sx={{ mb: "1.25rem" }}>
                <InlineMessage tone="success">
                  Everything is settled — assets returned, final pay run, exit steps done.
                </InlineMessage>
              </Box>
            )}

            <Box sx={{ fontSize: "0.875rem", color: tokens.text, lineHeight: 1.7, mb: "1rem" }}>
              {name} will become inactive. They lose access, leave the directory and
              come off payroll. Their record, documents and history stay.
            </Box>

            {blocked && (
              <>
                <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>
                  Why close it anyway? *
                </Box>
                <TextField
                  fullWidth size="small" multiline minRows={2} value={reason}
                  placeholder="e.g. Laptop written off; settlement paid by bank transfer outside PayLoom"
                  onChange={(e) => { setReason(e.target.value); setError(""); }}
                  error={Boolean(error)}
                />
                <ErrorMsg>{error}</ErrorMsg>
              </>
            )}
          </>
        )}
      </ModalBody>
      <ModalFoot note="Recorded on their career timeline, with who did it and when.">
        <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
        <BtnPrimary
          onClick={submit}
          disabled={busy || loading}
          sx={{ borderRadius: "8px", background: tokens.danger, "&:hover": { background: tokens.danger } }}
        >
          {busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : blocked ? "Close anyway" : "Mark as left"}
        </BtnPrimary>
      </ModalFoot>
    </Modal>
  );
};

export default ConfirmExitDialog;
